import { FunctionComponent, useEffect, useRef, useState } from "react";
import { getCityStateApi } from "../../../../service/repos/address/address_repo";

export interface PincodeDetail {
  pincode: number;
  city: string;
  state: string;
  country: string;
}

export type PincodeStatus =
  | "idle"
  | "checking"
  | "valid"
  | "invalid"
  | "unavailable";

export const isIndia = (country?: string | null) =>
  String(country ?? "").trim().toLowerCase() === "india";

// State lists differ across sources ("Andaman & Nicobar" vs "Andaman and Nicobar", and
// country-state-city merges Dadra/Daman into one entry), so compare letters only.
const stateKey = (s: string) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z]/g, "");

export const matchStateName = (state: string, options: string[]) => {
  const key = stateKey(state);
  if (!key) return undefined;
  return (
    options.find((o) => stateKey(o) === key) ??
    options.find((o) => {
      const k = stateKey(o);
      return Math.min(k.length, key.length) >= 5 && (k.includes(key) || key.includes(k));
    })
  );
};

/**
 * Looks up an Indian pincode whenever it reaches 6 digits. `onFound` (to prefill state/city) only
 * fires after `markTyped()`, so opening an existing address never overwrites what is saved.
 */
export const usePincodeCheck = ({
  pincode,
  country,
  onFound,
}: {
  pincode?: number | string | null;
  country?: string | null;
  onFound?: (detail: PincodeDetail) => void;
}) => {
  const [status, setStatus] = useState<PincodeStatus>("idle");
  const [detail, setDetail] = useState<PincodeDetail>();
  const [error, setError] = useState("");
  const typed = useRef(false);
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;

  const pin = String(pincode ?? "").trim();
  const india = isIndia(country);

  useEffect(() => {
    setDetail(undefined);
    setError("");
    if (!india || !/^\d{6}$/.test(pin)) {
      setStatus("idle");
      return;
    }
    let stale = false;
    setStatus("checking");
    getCityStateApi(Number(pin)).then((res: any) => {
      if (stale) return;
      if (res.success && res.data) {
        setStatus("valid");
        setDetail(res.data);
        if (typed.current) onFoundRef.current?.(res.data);
      } else if (res.code === "INVALID_PINCODE") {
        setStatus("invalid");
        setError(res.error);
      } else {
        setStatus("unavailable");
      }
      typed.current = false;
    });
    return () => {
      stale = true;
    };
  }, [pin, india]);

  /** Error to show on submit, or null when the pincode is fine (or could not be checked). */
  const validate = (state?: string | null): string | null => {
    if (!india || !pin) return null;
    if (!/^\d{6}$/.test(pin)) return "Pincode must be 6 digits";
    if (status === "checking") return "Pincode is still being verified, please wait";
    if (status === "invalid") return error;
    if (status === "valid" && detail && state && !matchStateName(detail.state, [state]))
      return `Pincode ${pin} is in ${detail.state}, not ${state}`;
    return null;
  };

  return {
    status,
    detail,
    error,
    validate,
    markTyped: () => {
      typed.current = true;
    },
  };
};

export type PincodeCheck = ReturnType<typeof usePincodeCheck>;

export const PincodeHint: FunctionComponent<{ check: PincodeCheck }> = ({
  check,
}) => {
  switch (check.status) {
    case "checking":
      return <p className="text-xs text-gray-500 mt-1">Checking pincode…</p>;
    case "valid":
      return (
        <p className="text-xs text-green-600 mt-1">
          {[check.detail?.city, check.detail?.state].filter(Boolean).join(", ")}
        </p>
      );
    case "invalid":
      return <p className="text-xs text-red-500 mt-1">{check.error}</p>;
    case "unavailable":
      return (
        <p className="text-xs text-amber-600 mt-1">
          Couldn't verify this pincode right now. Please double-check it.
        </p>
      );
    default:
      return null;
  }
};
